/* ═══════════════════════════════════════════════════════════════
   HeroAurora.jsx — OBSCURA hero section, "Aurora" variant
   Dark gradient bg, scanning document on the right, format
   ticker headline, Sapienza "UN PRODOTTO DI" pill below CTAs.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect } from "react";
import { F, type } from "../theme/tokens";
import {
  useScanCycle,
  ScanningDocument,
  FormatTicker,
} from "./HeroScanner";

/* ──────────────────────────────────────────────────────────────
   PROPS
     - onNavigate?:  () => void   click handler for the primary CTA
     - isMobile?:    boolean      viewport hint from your useViewport hook
     - isTablet?:    boolean
     - sapienzaLogo?: ReactNode | string
         · a string is rendered as <img src={...}/>
         · a ReactNode is rendered as-is
         · default: a placeholder SVG (see SapienzaPlaceholder below)
   ────────────────────────────────────────────────────────────── */

export default function HeroAurora({
  onNavigate = () => {},
  isMobile = false,
  isTablet = false,
  sapienzaLogo,
  gridPlusLogo,
  pageWrap,
  actions,
}) {
  const { idx, progress, displayed, typing } = useScanCycle();
  const wrapStyle = pageWrap || { maxWidth: 1240, margin: "0 auto", padding: "0 24px" };

  return (
    <section style={{
      position: "relative", overflowX: "hidden", overflowY: "visible",
      padding: isMobile ? "92px 0 52px" : "108px 0 64px",
      minHeight: isMobile ? "auto" : "100vh",
      display: "flex", alignItems: "flex-start",
      background: "radial-gradient(120% 80% at 20% 0%, #0c2244 0%, #061427 60%, #050f1f 100%)",
      color: "#fff",
    }}>
      <AuroraGlow />
      <GridOverlay />
      <DotField count={50} />
      {/* horizon glow */}
      <svg aria-hidden viewBox="0 0 1200 200" preserveAspectRatio="none"
        style={{ position: "absolute", left: 0, right: 0, bottom: 0, width: "100%", height: 200, pointerEvents: "none" }}>
        <defs>
          <linearGradient id="ob-horizon" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="transparent"/>
            <stop offset="100%" stopColor="rgba(0,156,221,.3)"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="1200" height="200" fill="url(#ob-horizon)"/>
      </svg>

      <div style={{ ...wrapStyle, position: "relative", zIndex: 1 }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1.05fr) minmax(0,.95fr)",
          gap: isMobile ? 36 : 48,
          alignItems: "flex-start",
        }}>
          {/* ─────── LEFT COLUMN ─────── */}
          <div>
            <Reveal>
              <h1 style={{
                ...type.h1,
                color: "#fff", marginBottom: 12,
              }}>
                Anonimizza documenti
                <br/>
                in italiano con AI
              </h1>
            </Reveal>

            <Reveal delay={100}>
              <div style={{ marginBottom: 14 }}>
                <FormatTicker idx={idx} displayed={displayed} typing={typing} dark />
              </div>
            </Reveal>

            <Reveal delay={160}>
              <p style={{
                fontSize: isMobile ? 16 : 18, lineHeight: 1.55,
                color: "rgba(255,255,255,.72)",
                maxWidth: 540, marginBottom: 20,
              }}>
                Obscura è una piattaforma per anonimizzare documenti PDF, immagini e documenti scansionati in italiano.
                Rileva automaticamente dati personali come nomi, codici fiscali, indirizzi, email, numeri di telefono e IBAN.
                Nessun file viene salvato sui nostri server.
              </p>
            </Reveal>

            <Reveal delay={220}>
              <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12, alignItems: isMobile ? "stretch" : "center" }}>
                {actions ?? (
                  <>
                    <PrimaryButton onClick={onNavigate}>Anonimizza un documento</PrimaryButton>
                    <GhostButton href="#contatti">Contattaci</GhostButton>
                  </>
                )}
              </div>
            </Reveal>
          </div>

          {/* ─────── RIGHT COLUMN ─────── */}
          <Reveal delay={180}>
            <ScanningDocument idx={idx} progress={progress} isMobile={isMobile} isTablet={isTablet} />
          </Reveal>
        </div>

        <Reveal delay={280}>
          <HeroTrustStrip isMobile={isMobile} sapienzaLogo={sapienzaLogo} gridPlusLogo={gridPlusLogo} />
        </Reveal>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════
   SUPPORT COMPONENTS
   Replace these with your own design-system primitives if you
   already have them.
   ══════════════════════════════════════════════════════════════ */

/* ── compact trust strip below hero grid ── */
function HeroTrustStrip({ isMobile, sapienzaLogo, gridPlusLogo }) {
  return (
    <div
      style={{
        marginTop: isMobile ? 44 : 76,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 30,
      }}
    >
      <span style={{ ...type.label, fontSize: 9, letterSpacing: ".18em", color: "rgba(255,255,255,0.38)", whiteSpace: "nowrap" }}>
        UN PRODOTTO DI
      </span>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
        {gridPlusLogo && (
          <>
            <img
              src={gridPlusLogo}
              alt="Logo GRID+"
              style={{ height: isMobile ? 36 : 44, maxWidth: 220, objectFit: "contain", filter: "brightness(1.05)" }}
            />
            <span style={{ width: 1, height: 22, background: "rgba(255,255,255,0.16)" }} />
          </>
        )}
        {renderLogo(sapienzaLogo, isMobile)}
      </div>
    </div>
  );
}

/* ── tiny in-view reveal (fades + slides up on mount) ── */
function Reveal({ children, delay = 0, y = 18 }) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSeen(true), 50);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{
      opacity: seen ? 1 : 0,
      transform: seen ? "none" : `translateY(${y}px)`,
      transition: `opacity .6s cubic-bezier(.22,1,.36,1) ${delay}ms, transform .6s cubic-bezier(.22,1,.36,1) ${delay}ms`,
    }}>{children}</div>
  );
}

/* ── primary CTA ── */
function PrimaryButton({ children, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 10,
        padding: "14px 26px",
        borderRadius: 12,
        background: "#fff",
        color: "#061427",
        border: "1px solid #fff",
        fontFamily: F.display, fontSize: 14, fontWeight: 700,
        cursor: "pointer",
        transition: "transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s",
        transform: h ? "translateY(-2px)" : "none",
        boxShadow: h ? "0 14px 30px rgba(0,156,221,.35)" : "0 4px 12px rgba(0,0,0,.18)",
      }}>
      {children}
      <span aria-hidden style={{ display: "inline-flex" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </span>
    </button>
  );
}

/* ── ghost CTA ── */
function GhostButton({ children, href }) {
  const [h, setH] = useState(false);
  return (
    <a href={href}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 10,
        padding: "14px 26px",
        borderRadius: 12,
        background: h ? "rgba(255,255,255,.08)" : "transparent",
        color: "#fff",
        border: "1px solid rgba(255,255,255,.22)",
        fontFamily: F.display, fontSize: 14, fontWeight: 700,
        textDecoration: "none",
        transition: "background .25s",
      }}>
      {children}
    </a>
  );
}

/* ── decorative atmospheric layers ── */
function AuroraGlow() {
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
      <div style={{
        position: "absolute", width: 700, height: 700, borderRadius: "50%",
        left: "-10%", top: "-20%",
        background: "radial-gradient(circle, rgba(89,201,244,.28), transparent 60%)",
        filter: "blur(40px)",
      }}/>
      <div style={{
        position: "absolute", width: 600, height: 600, borderRadius: "50%",
        right: "-10%", top: "20%",
        background: "radial-gradient(circle, rgba(0,156,221,.22), transparent 60%)",
        filter: "blur(50px)",
      }}/>
    </div>
  );
}
function GridOverlay({ size = 70 }) {
  return (
    <div aria-hidden style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      backgroundImage:
        "linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)",
      backgroundSize: `${size}px ${size}px`,
      opacity: .7,
      maskImage: "radial-gradient(ellipse at center, #000 30%, transparent 80%)",
      WebkitMaskImage: "radial-gradient(ellipse at center, #000 30%, transparent 80%)",
    }}/>
  );
}
function DotField({ count = 50 }) {
  const dots = React.useMemo(() => {
    return Array.from({ length: count }).map((_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      r: Math.random() * 1.8 + .4,
      o: Math.random() * .5 + .2,
      d: Math.random() * 3,
    }));
  }, [count]);
  return (
    <svg aria-hidden style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
      {dots.map((d, i) => (
        <circle key={i} cx={`${d.x}%`} cy={`${d.y}%`} r={d.r}
          fill="rgba(89,201,244,.6)" opacity={d.o}>
          <animate attributeName="opacity" values={`${d.o};${d.o * .3};${d.o}`}
            dur={`${3 + d.d}s`} repeatCount="indefinite"/>
        </circle>
      ))}
    </svg>
  );
}

/* ── Sapienza logo: pass a string (img src), a ReactNode, or
       leave it undefined to get the inline placeholder SVG ── */
function renderLogo(logo, isMobile = false) {
  if (typeof logo === "string") {
    return <img src={logo} alt="Sapienza Università di Roma" style={{
      height: isMobile ? 36 : 44, maxWidth: 220, objectFit: "contain", filter: "brightness(1.05)",
    }}/>;
  }
  if (logo) return logo;
  return <SapienzaPlaceholder />;
}

/* ── inline placeholder mark — swap with your real logo asset ── */
function SapienzaPlaceholder() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,.92)" }}>
      <svg width="22" height="22" viewBox="0 0 32 32" aria-hidden>
        <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" strokeWidth="1.3" opacity=".4"/>
        <path d="M16 6 L19 14 L27 14 L20.5 19 L23 27 L16 22 L9 27 L11.5 19 L5 14 L13 14 Z"
          fill="currentColor" opacity=".85"/>
      </svg>
      <span style={{
        fontFamily: F.display,
        fontSize: 12, fontWeight: 700, letterSpacing: ".06em",
        textTransform: "uppercase",
      }}>
        Sapienza · Startup
      </span>
    </span>
  );
}
